import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, TrendingUp, TrendingDown, 
  DollarSign, Calendar, Download, ArrowLeft, CheckCircle, Clock
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';

const GSTSummaryPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchGSTSummary();
    }
  }, [month, year, businessId]);

  const fetchGSTSummary = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.gstSummary({ month, year });
      setSummaryData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'gst-summary', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GST_Summary_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = summaryData?.summary || {
    outward_supplies: 0,
    inward_supplies: 0,
    output_gst: 0,
    input_gst: 0,
    net_gst_payable: 0,
    gst_paid: 0,
    balance: 0
  };

  const liability = summaryData?.liability_breakdown || {
    cgst: { output: 0, input: 0, net: 0 },
    sgst: { output: 0, input: 0, net: 0 },
    igst: { output: 0, input: 0, net: 0 },
    total: { output: 0, input: 0, net: 0 }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  return (
    <div className="h-full flex flex-col" data-testid="gst-summary-page">
      {/* Premium Header */}
      <ListPageHeader
        title="GST Summary"
        stats={[
          { icon: TrendingUp, label: 'Outward Supplies', value: formatCompactCurrency(summary.outward_supplies), color: 'blue' },
          { icon: TrendingDown, label: 'Inward Supplies', value: formatCompactCurrency(summary.inward_supplies), color: 'purple' },
          { icon: DollarSign, label: 'Net GST Payable', value: formatCompactCurrency(summary.net_gst_payable), color: 'emerald' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            {/* Back Button + Period Selector */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Reports
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading GST summary...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* GST Liability Summary Table */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">GST Liability Summary</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '28%' }}>Particulars</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '18%' }}>CGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '18%' }}>SGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '18%' }}>IGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '18%' }}>Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="tl-tr hover:bg-teal-50/40">
                      <td className="tl-td">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                          <span className="text-[13px] font-medium text-slate-700">Output GST</span>
                        </div>
                      </td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.cgst.output.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.sgst.output.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.igst.output.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-slate-800">₹{liability.total.output.toFixed(2)}</td>
                    </tr>
                    <tr className="tl-tr bg-[#FAFBFC] hover:bg-teal-50/40">
                      <td className="tl-td">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-purple-500" />
                          <span className="text-[13px] font-medium text-slate-700">Input GST (ITC)</span>
                        </div>
                      </td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.cgst.input.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.sgst.input.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{liability.igst.input.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-slate-800">₹{liability.total.input.toFixed(2)}</td>
                    </tr>
                    <tr className="bg-teal-50 border-t-2 border-teal-200">
                      <td className="tl-td">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-teal-600" />
                          <span className="text-[13px] font-bold text-slate-900">Net GST Payable</span>
                        </div>
                      </td>
                      <td className="tl-td text-right text-[13px] font-semibold text-teal-700">₹{liability.cgst.net.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-teal-700">₹{liability.sgst.net.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-teal-700">₹{liability.igst.net.toFixed(2)}</td>
                      <td className="tl-td text-right text-[14px] font-bold text-teal-800">₹{liability.total.net.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Status Cards */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">Payment Status</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  {/* Total Liability */}
                  <div className="p-5 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Total Liability</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-800">₹{summary.net_gst_payable.toFixed(2)}</div>
                  </div>

                  {/* Amount Paid */}
                  <div className="p-5 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-[11px] font-medium text-emerald-700 uppercase tracking-wider">Amount Paid</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700">₹{summary.gst_paid.toFixed(2)}</div>
                  </div>

                  {/* Balance Due */}
                  <div className="p-5 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-[11px] font-medium text-amber-700 uppercase tracking-wider">Balance Due</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-700">₹{summary.balance.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links to Other GST Reports */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">Related GST Reports</h3>
              </div>
              <div className="p-4 grid grid-cols-4 gap-3">
                {[
                  { title: 'GSTR-1', path: '/gst/gstr1', icon: FileText },
                  { title: 'GSTR-3B', path: '/gst/gstr3b', icon: FileSpreadsheet },
                  { title: 'Sales Register', path: '/gst/sales-register', icon: TrendingUp },
                  { title: 'Purchase Register', path: '/gst/purchase-register', icon: TrendingDown },
                ].map(report => {
                  const Icon = report.icon;
                  return (
                    <button
                      key={report.path}
                      onClick={() => navigate(`/business/${businessId}${report.path}`)}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg text-left hover:bg-teal-50 transition-all group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center group-hover:bg-teal-100">
                        <Icon className="w-4 h-4 text-slate-500 group-hover:text-teal-600" />
                      </div>
                      <span className="text-[12px] font-medium text-slate-700 group-hover:text-teal-700">{report.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GSTSummaryPage;
